import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Chip,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Repeat as RecurringIcon,
  Upload as ImportIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Settings as SettingsIcon,
  PlayArrow as CreateIcon,
  Print as PrintIcon,
  ClearAll as ClearIcon,
  DocumentScanner as ScanIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import BulkChequeGrid from './BulkChequeGrid';
import RecurringGenerationDialog from './RecurringGenerationDialog';
import ChequeImportDialog from './ChequeImportDialog';
import CopyFromExistingDialog from './CopyFromExistingDialog';
import PasteFromExcelDialog from './PasteFromExcelDialog';
import PaymentVoucherImportDialog from './PaymentVoucherImportDialog';
import PrintOptionsDialog from '../../common/Printing/PrintOptionsDialog';
import { PrintConfigurationDisplay } from '../../common/Printing';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { useBulkChequeGrid } from '../../../hooks/useBulkChequeGrid';
import { useBankService } from '../../../services/banks/bankService';
import { usePrintConfiguration } from '../../common/Printing/usePrintConfiguration';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { easyCheckBeneficiaryService } from '../../../services/beneficiaries/beneficiaryService';
import { chequeAttachmentService } from '../../../services/cheques/chequeAttachmentService';
import { documentTypeService } from '../../../services/documentTypes/documentTypeService';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { canAccess } from '../../../utils/planPermissions';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.2, 1, 0.2, 1),
  height: 'calc(100vh - 100px)',
  maxHeight: 'calc(100vh - 100px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(0.75, 0),
  flexShrink: 0,
}));

const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 0),
  flexShrink: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1),
}));

const BulkChequesUpgradePrompt = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', p: 4 }}>
    <Typography variant="h5" fontWeight={600} gutterBottom>
      Bulk Cheque Printing
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
      This feature is available on Business Plus and Premium plans. Please upgrade your plan to access bulk cheque printing.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Contact support@easycheckpro.com for upgrade options.
    </Typography>
  </Box>
);

const BulkChequesPage = () => {
  const { user } = useEasyCheck();

  const [selectedBankId, setSelectedBankId] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const selectedCurrency = useMemo(
    () => {
      const b = banks.find((bk) => (bk.Id || bk.id) === selectedBankId);
      return (b && (b.Currency || b.currency)) || 'AED';
    },
    [banks, selectedBankId]
  );
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);

  // Dialog states
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [printSettingsOpen, setPrintSettingsOpen] = useState(false);
  // Source PDF from the last voucher scan — attached to created cheques after CREATE ALL (Premium only).
  const [voucherFile, setVoucherFile] = useState(null);

  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { printConfig, updatePrintConfig } = usePrintConfiguration();
  const { getAllBanks } = useBankService();

  const {
    stagingRows,
    addRows,
    addEmptyRow,
    updateRow,
    deleteRow,
    clearAll,
    createAllCheques,
    printAllCheques,
    isCreating,
    isPrinting,
    selectedIds,
    setSelectedIds,
    total,
    stagedCount,
    createdCount,
    errorCount,
    selectedCount,
  } = useBulkChequeGrid();

  // Load banks on mount
  useEffect(() => {
    const loadBanks = async () => {
      setBanksLoading(true);
      try {
        const result = await getAllBanks(1, 1000);
        if (result?.success) {
          const bankData = Array.isArray(result.data) ? result.data : [];
          setBanks(bankData);
          if (bankData.length === 1) {
            setSelectedBankId(bankData[0].Id || bankData[0].id);
          }
        }
      } catch (err) {
        showError('Failed to load banks');
      } finally {
        setBanksLoading(false);
      }
    };
    loadBanks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load beneficiaries on mount
  useEffect(() => {
    const loadBeneficiaries = async () => {
      setBeneficiariesLoading(true);
      try {
        const result = await easyCheckBeneficiaryService.getAllBeneficiaries(1, 10000);
        if (result?.success) {
          const data = Array.isArray(result.data) ? result.data : [];
          setBeneficiaries(data.map(b => ({
            beneficiaryName: b.BeneficiaryName || b.beneficiaryName || '',
          })));
        }
      } catch (err) {
        console.error('Failed to load beneficiaries:', err);
      } finally {
        setBeneficiariesLoading(false);
      }
    };
    loadBeneficiaries();
  }, []);

  const handleBankChange = (event) => {
    setSelectedBankId(event.target.value);
  };

  const handleGenerate = useCallback((rows) => {
    addRows(rows);
    showSuccess(`${rows.length} recurring cheques added to grid`);
  }, [addRows, showSuccess]);

  const handleImportComplete = useCallback((rows) => {
    addRows(rows);
    showSuccess(`${rows.length} cheques imported to grid`);
  }, [addRows, showSuccess]);

  const handleCopyComplete = useCallback((rows) => {
    addRows(rows);
    showSuccess(`${rows.length} cheques copied to grid`);
  }, [addRows, showSuccess]);

  const handlePasteComplete = useCallback((rows) => {
    addRows(rows);
    showSuccess(`${rows.length} cheques added from paste`);
  }, [addRows, showSuccess]);

  const handleVoucherComplete = useCallback(({ rows, file }) => {
    addRows(rows);
    setVoucherFile(file || null);
    showSuccess(`${rows.length} voucher${rows.length === 1 ? '' : 's'} added to grid`);
  }, [addRows, showSuccess]);

  // Resolve (or create) the "Payment Voucher" document type used when attaching the source PDF.
  const resolveVoucherDocTypeId = useCallback(async () => {
    const res = await documentTypeService.getAll();
    const list = Array.isArray(res?.data) ? res.data : [];
    const existing = list.find(
      (dt) => (dt.TypeName || dt.typeName || '').trim().toLowerCase() === 'payment voucher'
    );
    if (existing) return existing.Id ?? existing.id;
    const created = await documentTypeService.create({ typeName: 'Payment Voucher' });
    return created?.data?.Id ?? created?.data?.id ?? null;
  }, []);

  // Best-effort: attach the scanned voucher PDF to each cheque it produced. Premium-only
  // (documentAttachments); silently skipped otherwise so scanning still works on lower plans.
  const attachVoucherToCheques = useCallback(async (createdResults) => {
    if (!voucherFile) return;
    if (!canAccess(user?.planId, 'documentAttachments')) return;
    const voucherCheques = (createdResults || []).filter(
      (c) => c.source === 'voucher' && c.serverChequeId
    );
    if (voucherCheques.length === 0) return;
    try {
      const docTypeId = await resolveVoucherDocTypeId();
      if (!docTypeId) return;
      let attached = 0;
      for (const c of voucherCheques) {
        const r = await chequeAttachmentService.uploadAttachment(c.serverChequeId, voucherFile, docTypeId);
        if (r?.success) attached += 1;
      }
      if (attached > 0) {
        showSuccess(`Voucher attached to ${attached} cheque${attached === 1 ? '' : 's'}`);
      }
      setVoucherFile(null);
    } catch (err) {
      console.warn('[voucher] attachment failed:', err);
    }
  }, [voucherFile, user?.planId, resolveVoucherDocTypeId, showSuccess]);

  const handleCreateAll = async () => {
    if (!selectedBankId) {
      showError('Please select a bank first');
      return;
    }
    if (stagedCount === 0) {
      showError('No staged cheques to create');
      return;
    }
    const createdResults = await createAllCheques(selectedBankId);
    showSuccess('Cheque creation completed');
    await attachVoucherToCheques(createdResults);
  };

  const handlePrintAll = async () => {
    if (createdCount === 0) {
      showError('No created cheques to print');
      return;
    }
    const result = await printAllCheques(printConfig);
    if (result?.success) {
      const printedCount = selectedCount > 0 ? selectedCount : createdCount;
      showSuccess(`PDF opened with ${printedCount} cheque${printedCount === 1 ? '' : 's'}`);
    } else {
      showError(result?.message || 'Print failed');
    }
  };

  const handleClearAll = () => {
    clearAll();
    setVoucherFile(null);
    showSuccess('Grid cleared');
  };

  // Plan-based access guard
  if (!canAccess(user?.planId, 'bulkCheques')) {
    return <BulkChequesUpgradePrompt />;
  }

  return (
    <PageContainer>
      {/* Header */}
      <HeaderSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h4" fontWeight={700} color="primary">
            Bulk Cheques
          </Typography>
          <FormControl size="small" sx={{ minWidth: 320, my: 1.5 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Select Bank Account</InputLabel>
            <Select
              value={selectedBankId}
              onChange={handleBankChange}
              label="Select Bank Account"
              disabled={banksLoading}
              endAdornment={banksLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              sx={{
                borderRadius: 0,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '0px !important',
                }
              }}
            >
              {banks.map(bank => (
                <MenuItem key={bank.Id || bank.id} value={bank.Id || bank.id} sx={{ fontSize: '0.875rem' }}>
                  {bank.AccountNumber || bank.accountNumber} - {bank.AccountName || bank.accountName} ({bank.Currency || bank.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedBankId && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<RecurringIcon />}
              onClick={() => setRecurringOpen(true)}
              disabled={isCreating || isPrinting}
              size="small"
            >
              RECURRING
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => {
                if (!selectedBankId) {
                  showError('Please select a bank account first');
                  return;
                }
                setImportOpen(true);
              }}
              disabled={isCreating || isPrinting}
              size="small"
            >
              IMPORT
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addEmptyRow}
              disabled={isCreating || isPrinting}
              size="small"
            >
              ADD ROW
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => setCopyOpen(true)}
              disabled={isCreating || isPrinting}
              size="small"
            >
              COPY EXISTING
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<PasteIcon />}
              onClick={() => {
                if (!selectedBankId) {
                  showError('Please select a bank account first');
                  return;
                }
                setPasteOpen(true);
              }}
              disabled={isCreating || isPrinting}
              size="small"
            >
              PASTE
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<ScanIcon />}
              onClick={() => {
                if (!selectedBankId) {
                  showError('Please select a bank account first');
                  return;
                }
                setVoucherOpen(true);
              }}
              disabled={isCreating || isPrinting}
              size="small"
            >
              SCAN VOUCHER
            </EasyCheckButton>
            <Tooltip title="Configure Print Options">
              <IconButton
                size="small"
                onClick={() => setPrintSettingsOpen(true)}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </HeaderSection>

      {/* Print Configuration Display */}
      <Box sx={{ mb: 0.5 }}>
        <PrintConfigurationDisplay
          printConfig={printConfig}
          variant="default"
          size="small"
          showTitle={false}
        />
      </Box>

      {/* Grid */}
      <BulkChequeGrid
        rows={stagingRows}
        onProcessRowUpdate={updateRow}
        onDeleteRow={deleteRow}
        loading={isCreating}
        emptyMessage={selectedBankId ? 'No cheques in staging grid' : 'Select a bank account to get started'}
        emptySubMessage={selectedBankId ? 'Use the action buttons above to add cheques' : 'Choose a bank account from the dropdown above'}
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={setSelectedIds}
        currency={selectedCurrency}
      />

      {/* Action Bar */}
      <ActionBar>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Total: ${total}`} size="small" variant="outlined" />
          <Chip label={`Staged: ${stagedCount}`} size="small" color="default" variant="outlined" />
          <Chip label={`Created: ${createdCount}`} size="small" color="success" variant="outlined" />
          {selectedCount > 0 && (
            <Chip label={`Selected: ${selectedCount}`} size="small" color="primary" variant="outlined" />
          )}
          {errorCount > 0 && (
            <Chip label={`Errors: ${errorCount}`} size="small" color="error" variant="outlined" />
          )}
        </Stack>

        <Stack direction="row" spacing={1.5}>
          <EasyCheckButton
            buttonType="action"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearAll}
            disabled={total === 0 || isCreating || isPrinting}
            size="small"
          >
            CLEAR ALL
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            startIcon={isCreating ? <CircularProgress size={18} color="inherit" /> : <CreateIcon />}
            onClick={handleCreateAll}
            disabled={stagedCount === 0 || isCreating || isPrinting || !selectedBankId}
            size="small"
          >
            CREATE ALL ({stagedCount})
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            color="success"
            startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={handlePrintAll}
            disabled={createdCount === 0 || isCreating || isPrinting}
            size="small"
          >
            {selectedCount > 0
              ? `PRINT SELECTED (${selectedCount})`
              : `PRINT ALL (${createdCount})`}
          </EasyCheckButton>
        </Stack>
      </ActionBar>

      {/* Dialogs */}
      <RecurringGenerationDialog
        open={recurringOpen}
        onClose={() => setRecurringOpen(false)}
        onGenerate={handleGenerate}
        bankId={selectedBankId}
        currency={selectedCurrency}
        beneficiaries={beneficiaries}
        beneficiariesLoading={beneficiariesLoading}
      />

      <ChequeImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={handleImportComplete}
        bankId={selectedBankId}
      />

      <CopyFromExistingDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        onCopyComplete={handleCopyComplete}
        bankId={selectedBankId}
        currency={selectedCurrency}
      />

      <PasteFromExcelDialog
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onPasteComplete={handlePasteComplete}
        bankId={selectedBankId}
      />

      <PaymentVoucherImportDialog
        open={voucherOpen}
        onClose={() => setVoucherOpen(false)}
        onComplete={handleVoucherComplete}
        bankId={selectedBankId}
        currency={selectedCurrency}
      />

      <PrintOptionsDialog
        open={printSettingsOpen}
        onClose={() => setPrintSettingsOpen(false)}
        printConfig={printConfig}
        onConfigChange={updatePrintConfig}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </PageContainer>
  );
};

export default BulkChequesPage;
